import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DollarSign,
  RefreshCw,
  FileSpreadsheet,
  FileText,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Receipt,
  Wallet,
} from "lucide-react";
import {
  getFinancialReport,
  generateReportPdf,
  type FinancialReport,
} from "@/utils/reports.functions";
import { downloadCsv, downloadPdfFromBase64 } from "@/lib/report-export";
import { useLocaleFormat } from "@/lib/i18n-format";
import { PdfPreviewDialog } from "@/components/reports/PdfPreviewDialog";

export const Route = createFileRoute("/_app/reports/financial")({
  component: FinancialReportPage,
});

function todayPlusYearISO() {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

// Período fixo "todo o histórico" — sem filtro de data na UI
const PERIOD_START = "2000-01-01";

function FinancialReportPage() {
  const { t } = useTranslation();
  const { company } = useAuth();
  const { formatMoney, formatDate } = useLocaleFormat();
  const currency = company?.currency ?? "USD";

  const periodStart = PERIOD_START;
  const periodEnd = todayPlusYearISO();
  const [clientFilter, setClientFilter] = useState<string>("all");
  const [contractFilter, setContractFilter] = useState<string>("all");
  const [projectFilter, setProjectFilter] = useState<string>("all");
  const [data, setData] = useState<FinancialReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState<"csv" | "pdf" | null>(null);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewBase64, setPdfPreviewBase64] = useState<string | null>(null);
  const [pdfPreviewFilename, setPdfPreviewFilename] = useState<string>("");
  const [clients, setClients] = useState<Array<{ id: string; name: string }>>([]);
  const [contracts, setContracts] = useState<
    Array<{ id: string; number: string; title: string; client_id: string | null }>
  >([]);
  const [projects, setProjects] = useState<
    Array<{
      id: string;
      name: string;
      code: string;
      client_id: string | null;
      contract_id: string | null;
    }>
  >([]);

  const reportFn = useServerFn(getFinancialReport);
  const pdfFn = useServerFn(generateReportPdf);

  useEffect(() => {
    if (!company?.id) return;
    void supabase
      .from("clients")
      .select("id, name")
      .eq("company_id", company.id)
      .eq("is_active", true)
      .order("name")
      .then(({ data }) => setClients((data ?? []) as any));
    void supabase
      .from("contracts")
      .select("id, number, title, client_id")
      .eq("company_id", company.id)
      .order("number", { ascending: false })
      .then(({ data }) => setContracts((data ?? []) as any));
    void supabase
      .from("projects")
      .select("id, name, code, client_id, contract_id")
      .eq("company_id", company.id)
      .order("name")
      .then(({ data }) => setProjects((data ?? []) as any));
  }, [company?.id]);

  // Reset cascading filters
  useEffect(() => {
    if (clientFilter !== "all" && contractFilter !== "all") {
      const c = contracts.find((x) => x.id === contractFilter);
      if (c && c.client_id && c.client_id !== clientFilter) setContractFilter("all");
    }
    if (clientFilter !== "all" && projectFilter !== "all") {
      const p = projects.find((x) => x.id === projectFilter);
      if (p && p.client_id && p.client_id !== clientFilter) setProjectFilter("all");
    }
    if (contractFilter !== "all" && projectFilter !== "all") {
      const p = projects.find((x) => x.id === projectFilter);
      if (p && p.contract_id && p.contract_id !== contractFilter) setProjectFilter("all");
    }
  }, [clientFilter, contractFilter, projectFilter, contracts, projects]);

  const visibleContracts =
    clientFilter === "all" ? contracts : contracts.filter((c) => c.client_id === clientFilter);

  const visibleProjects = projects.filter((p) => {
    if (clientFilter !== "all" && p.client_id !== clientFilter) return false;
    if (contractFilter !== "all" && p.contract_id !== contractFilter) return false;
    return true;
  });

  const load = async () => {
    if (!periodStart || !periodEnd) return;
    setLoading(true);
    try {
      const r = await reportFn({
        data: {
          periodStart,
          periodEnd,
          clientId: clientFilter === "all" ? null : clientFilter,
          contractId: contractFilter === "all" ? null : contractFilter,
          projectId: projectFilter === "all" ? null : projectFilter,
        },
      });
      setData(r);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("pages.reports.common.loadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientFilter, contractFilter, projectFilter]);

  const handleExportCsv = () => {
    if (!data) return;
    setExporting("csv");
    try {
      const headers = [
        t("pages.reports.financial.csvHeaders.number"),
        t("pages.reports.financial.csvHeaders.title"),
        t("pages.reports.financial.csvHeaders.client"),
        t("pages.reports.financial.csvHeaders.issueDate"),
        t("pages.reports.financial.csvHeaders.dueDate"),
        t("pages.reports.financial.csvHeaders.total"),
        t("pages.reports.financial.csvHeaders.paid"),
        t("pages.reports.financial.csvHeaders.balance"),
        t("pages.reports.financial.csvHeaders.status"),
      ];
      const rows = data.recentInvoices.map((i) => [
        i.number,
        i.title,
        i.client,
        i.issueDate,
        i.dueDate ?? "",
        i.total,
        i.paid,
        Math.max(0, i.total - i.paid),
        i.status,
      ]);
      downloadCsv(
        `${t("pages.reports.financial.csvFilenamePrefix")}-${periodStart}-${periodEnd}.csv`,
        headers,
        rows,
      );
    } finally {
      setExporting(null);
    }
  };

  const handleExportPdf = async () => {
    if (!periodStart || !periodEnd) {
      toast.error(t("pages.reports.common.loadError"));
      return;
    }
    setExporting("pdf");
    try {
      const { pdfBase64, filename } = await pdfFn({
        data: {
          kind: "financial",
          periodStart,
          periodEnd,
          clientId: clientFilter === "all" ? null : clientFilter,
          contractId: contractFilter === "all" ? null : contractFilter,
          projectId: projectFilter === "all" ? null : projectFilter,
        },
      });
      setPdfPreviewBase64(pdfBase64);
      setPdfPreviewFilename(filename);
      setPdfPreviewOpen(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("pages.reports.common.pdfError"));
    } finally {
      setExporting(null);
    }
  };

  const confirmDownloadPdf = () => {
    if (!pdfPreviewBase64) return;
    downloadPdfFromBase64(pdfPreviewFilename, pdfPreviewBase64);
    setPdfPreviewOpen(false);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-primary" />
            {t("pages.reports.common.filters")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5">
              <Label>{t("pages.reports.financial.filters.client", "Cliente")}</Label>
              <Select value={clientFilter} onValueChange={setClientFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t("pages.reports.financial.filters.allClients", "Todos os clientes")}
                  </SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("pages.reports.financial.filters.contract", "Contrato")}</Label>
              <Select value={contractFilter} onValueChange={setContractFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t("pages.reports.financial.filters.allContracts", "Todos os contratos")}
                  </SelectItem>
                  {visibleContracts.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.number} — {c.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("pages.reports.financial.filters.project", "Obra")}</Label>
              <Select value={projectFilter} onValueChange={setProjectFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t("pages.reports.financial.filters.allProjects", "Todas as obras")}
                  </SelectItem>
                  {visibleProjects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.code ? `${p.code} — ${p.name}` : p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={load} disabled={loading} className="w-full">
                <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                {t("pages.reports.common.refresh")}
              </Button>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCsv}
              disabled={!data || exporting !== null}
            >
              <FileSpreadsheet className="mr-1.5 h-4 w-4" />
              {t("pages.reports.financial.exportCsvInvoices")}
            </Button>
            <Button size="sm" onClick={handleExportPdf} disabled={!data || exporting !== null}>
              {exporting === "pdf" ? (
                <RefreshCw className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <FileText className="mr-1.5 h-4 w-4" />
              )}
              {t("pages.reports.common.exportPdf")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-72 w-full" />
        </div>
      ) : data ? (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              icon={<TrendingUp className="h-4 w-4" />}
              label={t("pages.reports.financial.kpis.received")}
              value={formatMoney(data.payments.total, currency)}
              hint={t("pages.reports.financial.kpis.receivedHint", { count: data.payments.count })}
              tone="success"
            />
            <KpiCard
              icon={<Receipt className="h-4 w-4" />}
              label={t("pages.reports.financial.kpis.invoiced")}
              value={formatMoney(data.invoicing.issuedTotal, currency)}
              hint={t("pages.reports.financial.kpis.invoicedHint", {
                count: data.invoicing.issuedCount,
              })}
            />
            <KpiCard
              icon={<TrendingDown className="h-4 w-4" />}
              label={t("pages.reports.financial.kpis.expenses")}
              value={formatMoney(data.expenses.total, currency)}
              hint={t("pages.reports.financial.kpis.expensesHint", { count: data.expenses.count })}
              tone="danger"
            />
            <KpiCard
              icon={<AlertTriangle className="h-4 w-4" />}
              label={t("pages.reports.financial.kpis.overdue")}
              value={formatMoney(data.invoicing.overdueTotal, currency)}
              hint={t("pages.reports.financial.kpis.overdueHint", {
                count: data.invoicing.overdueCount,
              })}
              tone={data.invoicing.overdueCount > 0 ? "warn" : undefined}
            />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("pages.reports.financial.billingSummary")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <Row
                  label={t("pages.reports.financial.rows.issued")}
                  value={formatMoney(data.invoicing.issuedTotal, currency)}
                />
                <Row
                  label={t("pages.reports.financial.rows.paid")}
                  value={formatMoney(data.invoicing.paidTotal, currency)}
                />
                <Row
                  label={t("pages.reports.financial.rows.outstanding")}
                  value={formatMoney(data.invoicing.outstandingTotal, currency)}
                />
                <Row
                  label={t("pages.reports.financial.rows.overdue")}
                  value={formatMoney(data.invoicing.overdueTotal, currency)}
                  warn={data.invoicing.overdueTotal > 0}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("pages.reports.financial.expensesByCategory")}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {data.expenses.byCategory.length === 0 ? (
                  <p className="p-6 text-sm text-muted-foreground">
                    {t("pages.reports.financial.noExpenses")}
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("pages.reports.financial.tableHeaders.category")}</TableHead>
                        <TableHead className="text-right">
                          {t("pages.reports.financial.tableHeaders.qty")}
                        </TableHead>
                        <TableHead className="text-right">
                          {t("pages.reports.financial.tableHeaders.total")}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.expenses.byCategory.map((c) => (
                        <TableRow key={c.category}>
                          <TableCell className="capitalize">{c.category}</TableCell>
                          <TableCell className="text-right tabular-nums">{c.count}</TableCell>
                          <TableCell className="text-right font-medium tabular-nums">
                            {formatMoney(c.total, currency)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>

          {data.topClients.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Wallet className="h-4 w-4" />
                  {t("pages.reports.financial.topClientsTitle")}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("pages.reports.financial.tableHeaders.client")}</TableHead>
                      <TableHead className="text-right">
                        {t("pages.reports.financial.tableHeaders.paidInvoices")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("pages.reports.financial.tableHeaders.totalReceived")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.topClients.map((c) => (
                      <TableRow key={c.clientId}>
                        <TableCell>{c.name}</TableCell>
                        <TableCell className="text-right tabular-nums">{c.invoices}</TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {formatMoney(c.paid, currency)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t("pages.reports.financial.invoicesInPeriod")}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {data.recentInvoices.length === 0 ? (
                <p className="p-6 text-sm text-muted-foreground">
                  {t("pages.reports.financial.noInvoices")}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("pages.reports.financial.tableHeaders.number")}</TableHead>
                      <TableHead>{t("pages.reports.financial.tableHeaders.client")}</TableHead>
                      <TableHead>{t("pages.reports.financial.tableHeaders.issueDate")}</TableHead>
                      <TableHead>{t("pages.reports.financial.tableHeaders.dueDate")}</TableHead>
                      <TableHead className="text-right">
                        {t("pages.reports.financial.tableHeaders.total")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("pages.reports.financial.tableHeaders.paid")}
                      </TableHead>
                      <TableHead>{t("pages.reports.financial.tableHeaders.status")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.recentInvoices.map((i) => (
                      <TableRow key={i.id}>
                        <TableCell className="font-mono text-xs">{i.number}</TableCell>
                        <TableCell>{i.client}</TableCell>
                        <TableCell className="text-sm">{formatDate(i.issueDate)}</TableCell>
                        <TableCell className="text-sm">
                          {i.dueDate ? formatDate(i.dueDate) : "—"}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatMoney(i.total, currency)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatMoney(i.paid, currency)}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={i.status} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}

      <PdfPreviewDialog
        open={pdfPreviewOpen}
        onOpenChange={setPdfPreviewOpen}
        pdfBase64={pdfPreviewBase64}
        filename={pdfPreviewFilename}
        onDownload={confirmDownloadPdf}
      />
    </div>
  );
}

function Row({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="flex items-center justify-between border-b border-border/50 py-2 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-medium tabular-nums ${warn ? "text-destructive" : ""}`}>{value}</span>
    </div>
  );
}

function KpiCard({
  icon,
  label,
  value,
  hint,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  tone?: "success" | "warn" | "danger";
}) {
  const toneClass =
    tone === "success"
      ? "text-emerald-600 dark:text-emerald-400"
      : tone === "warn"
        ? "text-amber-600 dark:text-amber-400"
        : tone === "danger"
          ? "text-destructive"
          : "text-foreground";
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
          {icon}
          {label}
        </div>
        <div className={`mt-2 text-2xl font-bold tabular-nums ${toneClass}`}>{value}</div>
        {hint && <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }: { status: string }) {
  const variant =
    status === "paid"
      ? "default"
      : status === "overdue"
        ? "destructive"
        : status === "draft" || status === "cancelled"
          ? "outline"
          : "secondary";
  return (
    <Badge variant={variant as any} className="capitalize">
      {status}
    </Badge>
  );
}
